// Parity P1 wave 1: the task layout document (hybrid design §3.3; contract `TaskLayout`,
// packages/schema contracts/api/task-detail.ts) and the pure edits the layout editor makes on a
// draft. Nothing here writes: the editor changes a draft and only "Save" sends it, so a drag
// moves the preview and nothing else (P1 acceptance "Layout drag updates only the preview").
//
// Schema: sections → columns (width of 12) → items, an item being a block id or one titled
// group of block ids (one nesting level, as Work OS). Core properties (title, tags, status,
// assignee, delegate, dates, priority) are fixed and never placed (D6).
import type { OperationOutput } from "@companyos/sdk";

export type TaskLayout = OperationOutput<"taskLayout.board.get">["effective"];
export type LayoutSection = TaskLayout["sections"][number];
export type LayoutColumn = LayoutSection["columns"][number];
export type LayoutItem = LayoutColumn["items"][number];
export type LayoutGroup = Exclude<LayoutItem, string>;

/** Where an item sits: section, column, index in the column (and in a group, its index). */
export type ItemPath = { section: number; column: number; index: number; inGroup?: number };

/**
 * The built-in layout a board without its own (and without a space default) uses. Mirrors the
 * API's default (services/api, P1 task layout); the server's `effective` wins whenever loaded.
 */
export const DEFAULT_LAYOUT: TaskLayout = {
  version: 1,
  sections: [
    {
      id: "overview",
      title: "Overview",
      columns: [{ width: 12, items: ["description", "checklists"] }],
    },
    {
      id: "delivery",
      title: "Delivery",
      columns: [{ width: 12, items: ["subtasks", "dependencies", "links", "files", "time"] }],
    },
    { id: "metrics", title: "Metrics", columns: [{ width: 12, items: ["metrics"] }] },
  ],
  hidden: [],
};

/** Presets of "+ Add section" (design §3.3: one wide, halves, wide + narrow, thirds). */
export const SECTION_PRESETS: { id: string; label: string; widths: number[] }[] = [
  { id: "wide", label: "One wide", widths: [12] },
  { id: "halves", label: "Halves", widths: [6, 6] },
  { id: "wide-narrow", label: "Wide + narrow", widths: [8, 4] },
  { id: "thirds", label: "Thirds", widths: [4, 4, 4] },
];

const clone = (l: TaskLayout): TaskLayout => structuredClone(l);

/** Every block id an item names (a group's blocks included). */
export function itemBlocks(item: LayoutItem): string[] {
  return typeof item === "string" ? [item] : item.group.blocks;
}

/** Every block id the layout places, in reading order. */
export function placedBlocks(layout: TaskLayout): string[] {
  return layout.sections.flatMap((s) => s.columns.flatMap((c) => c.items.flatMap(itemBlocks)));
}

/**
 * The shelf (design §3.3 "Not placed"): blocks the build knows (registered blocks and the
 * board's fields as `field:<id>`) that the layout does not place, in the order given. A field
 * covered by `fields:rest` is placed (it renders there) unless it has a block of its own.
 */
export function shelfOf(layout: TaskLayout, known: string[]): string[] {
  const placed = new Set(placedBlocks(layout));
  const rest = placed.has("fields:rest");
  return known.filter((id) => !placed.has(id) && !(rest && id.startsWith("field:")));
}

/** The fields `fields:rest` renders: the board's fields without a block of their own. */
export function restFields(layout: TaskLayout, fieldIds: string[]): string[] {
  const own = new Set(placedBlocks(layout).filter((b) => b.startsWith("field:")));
  return fieldIds.filter((id) => !own.has(`field:${id}`));
}

/** A section id not taken in the layout ("section-2", …). */
export function freshSectionId(layout: TaskLayout, base = "section"): string {
  const taken = new Set(layout.sections.map((s) => s.id));
  if (!taken.has(base)) return base;
  for (let i = 2; ; i++) if (!taken.has(`${base}-${i}`)) return `${base}-${i}`;
}

export function addSection(layout: TaskLayout, preset: string, title: string): TaskLayout {
  const widths = SECTION_PRESETS.find((p) => p.id === preset)?.widths ?? [12];
  const out = clone(layout);
  if (out.sections.length >= 20) return out;
  out.sections.push({
    id: freshSectionId(out),
    title,
    columns: widths.map((width) => ({ width, items: [] })),
  });
  return out;
}

export function removeSection(layout: TaskLayout, section: number): TaskLayout {
  const out = clone(layout);
  out.sections.splice(section, 1);
  return out;
}

export function updateSection(
  layout: TaskLayout,
  section: number,
  patch: Partial<Pick<LayoutSection, "title" | "hidden" | "accent" | "icon">>,
): TaskLayout {
  const out = clone(layout);
  const s = out.sections[section];
  if (!s) return out;
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === "") delete (s as Record<string, unknown>)[k];
    else (s as Record<string, unknown>)[k] = v;
  }
  return out;
}

/** Moves a section up or down (`delta` −1 / +1); out of range is a no-op. */
export function moveSection(layout: TaskLayout, section: number, delta: number): TaskLayout {
  const to = section + delta;
  if (to < 0 || to >= layout.sections.length) return layout;
  const out = clone(layout);
  const [s] = out.sections.splice(section, 1);
  if (s) out.sections.splice(to, 0, s);
  return out;
}

/** Adds a column at the end of a section (at most four); it takes width 4. */
export function addColumn(layout: TaskLayout, section: number): TaskLayout {
  const out = clone(layout);
  const s = out.sections[section];
  if (!s || s.columns.length >= 4) return out;
  s.columns.push({ width: 4, items: [] });
  return out;
}

/** Removes a column; its content moves to the column on its left (or right, for the first). */
export function removeColumn(layout: TaskLayout, section: number, column: number): TaskLayout {
  const out = clone(layout);
  const s = out.sections[section];
  if (!s || s.columns.length <= 1) return out;
  const [gone] = s.columns.splice(column, 1);
  const heir = s.columns[Math.max(0, column - 1)];
  if (gone && heir) heir.items.push(...gone.items);
  return out;
}

/** Steps a column's width by `delta` (2..12). */
export function resizeColumn(
  layout: TaskLayout,
  section: number,
  column: number,
  delta: number,
): TaskLayout {
  const out = clone(layout);
  const c = out.sections[section]?.columns[column];
  if (!c) return out;
  c.width = Math.min(12, Math.max(2, c.width + delta));
  return out;
}

/** Moves a whole column to another section (appended there, at most four per section). */
export function moveColumn(
  layout: TaskLayout,
  from: { section: number; column: number },
  toSection: number,
): TaskLayout {
  if (from.section === toSection) return layout;
  const out = clone(layout);
  const src = out.sections[from.section];
  const dst = out.sections[toSection];
  if (!src || !dst || dst.columns.length >= 4 || src.columns.length <= 1) return layout;
  const [col] = src.columns.splice(from.column, 1);
  if (col) dst.columns.push(col);
  return out;
}

/** Removes one block from wherever it is placed (a group left empty stays, titled). */
export function removeBlock(layout: TaskLayout, block: string): TaskLayout {
  const out = clone(layout);
  removeIn(out, block);
  return out;
}

/** Removes `block` in place, keeping every other item (and group) the same object. */
function removeIn(out: TaskLayout, block: string) {
  for (const s of out.sections)
    for (const c of s.columns) {
      for (const it of c.items)
        if (typeof it !== "string") it.group.blocks = it.group.blocks.filter((b) => b !== block);
      c.items = c.items.filter((it) => it !== block);
    }
}

/**
 * Places a block (from the shelf or from elsewhere in the layout) in a column at `index`, or
 * inside a group at that column index when `intoGroup` names it. The block leaves its old place.
 * `index` and `intoGroup` are positions in the layout as given (before the block leaves).
 */
export function placeBlock(
  layout: TaskLayout,
  block: string,
  to: { section: number; column: number; index?: number; intoGroup?: number },
): TaskLayout {
  const out = clone(layout);
  const col = out.sections[to.section]?.columns[to.column];
  if (!col) return layout;
  // Resolve the destination before the block leaves its place (which may shift the column).
  const group = to.intoGroup !== undefined ? col.items[to.intoGroup] : undefined;
  const wanted = Math.min(Math.max(0, to.index ?? col.items.length), col.items.length);
  const shift = col.items.slice(0, wanted).filter((it) => it === block).length;
  removeIn(out, block);
  out.hidden = out.hidden.filter((h) => h !== block);
  if (group && typeof group !== "string") {
    group.group.blocks.push(block);
    return out;
  }
  if (to.intoGroup !== undefined) return layout;
  const items = out.sections[to.section]?.columns[to.column]?.items ?? [];
  items.splice(Math.min(wanted - shift, items.length), 0, block);
  return out;
}

/** Moves an item within its column by `delta` (the keyboard twin of a drag: Alt+↑/↓). */
export function nudgeItem(
  layout: TaskLayout,
  at: { section: number; column: number; index: number },
  delta: number,
): TaskLayout {
  const out = clone(layout);
  const items = out.sections[at.section]?.columns[at.column]?.items;
  if (!items) return layout;
  const to = at.index + delta;
  if (to < 0 || to >= items.length) return layout;
  const [it] = items.splice(at.index, 1);
  if (it !== undefined) items.splice(to, 0, it);
  return out;
}

/** Adds an empty titled group at the end of a column. */
export function addGroup(
  layout: TaskLayout,
  section: number,
  column: number,
  title: string,
): TaskLayout {
  const out = clone(layout);
  const col = out.sections[section]?.columns[column];
  if (!col) return layout;
  col.items.push({ group: { title, blocks: [] } });
  return out;
}

/** Removes a group; its blocks stay in the column, where the group was. */
export function ungroup(
  layout: TaskLayout,
  at: { section: number; column: number; index: number },
): TaskLayout {
  const out = clone(layout);
  const col = out.sections[at.section]?.columns[at.column];
  const g = col?.items[at.index];
  if (!col || !g || typeof g === "string") return layout;
  col.items.splice(at.index, 1, ...g.group.blocks);
  return out;
}

/** Whether two layouts are the same document (the editor's "unsaved changes"). */
export function sameLayout(a: TaskLayout | null, b: TaskLayout | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** The title a block id reads as (`field:<id>` by the field's name). */
export function blockTitle(
  id: string,
  titles: Map<string, string>,
  fieldName: (fieldId: string) => string | undefined,
): string {
  if (id.startsWith("field:")) return fieldName(id.slice(6)) ?? "A removed field";
  if (id === "fields:rest") return "Other fields";
  return titles.get(id) ?? id;
}
